import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Mail, Save } from 'lucide-react';
import { getEmailTemplates, updateEmailTemplate, type EmailTemplate } from '../lib/api';
import './AdminEmailTemplatesPage.css';

export default function AdminEmailTemplatesPage() {
  const { getAccessTokenSilently } = useAuth0();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedKey, setSelectedKey] = useState('');
  const [draft, setDraft] = useState({ subject: '', text_body: '', html_body: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const selectedTemplate = templates.find((template) => template.key === selectedKey);

  useEffect(() => {
    let active = true;
    void getAccessTokenSilently().then((token) => {
      if (!token) throw new Error('Missing access token');
      return getEmailTemplates(token);
    }).then(({ templates: loaded }) => {
      if (!active) return;
      setTemplates(loaded);
      setSelectedKey(loaded[0]?.key ?? '');
      if (loaded[0]) setDraft({ subject: loaded[0].subject, text_body: loaded[0].text_body, html_body: loaded[0].html_body });
    }).catch((loadError: unknown) => {
      if (active) setError(loadError instanceof Error ? loadError.message : 'Unable to load email templates.');
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [getAccessTokenSilently]);

  const selectTemplate = (template: EmailTemplate) => {
    setSelectedKey(template.key);
    setDraft({ subject: template.subject, text_body: template.text_body, html_body: template.html_body });
    setError('');
    setNotice('');
  };

  const saveTemplate = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedTemplate) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const token = await getAccessTokenSilently();
      if (!token) throw new Error('Missing access token');
      const saved = await updateEmailTemplate(token, selectedTemplate.key, draft);
      setTemplates((current) => current.map((template) => template.key === saved.key ? saved : template));
      setNotice('Template saved');
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Unable to save this template.');
    } finally {
      setSaving(false);
    }
  };

  return <>
    <div className="facility-page-heading"><div><p className="eyebrow">Administration</p><h1>Email Templates</h1><p>Edit the messages Actapet sends to pet owners and facilities.</p></div></div>
    {error && <p className="form-error facility-error" role="alert">{error}</p>}
    <div className="email-template-layout">
      <aside className="email-template-list" aria-label="Email templates">
        <h2>Templates</h2>
        {loading ? <p className="facility-empty">Loading templates...</p> : templates.map((template) => <button type="button" key={template.key} className={selectedKey === template.key ? 'active' : ''} onClick={() => selectTemplate(template)}><Mail size={15} /><span>{template.name}</span></button>)}
      </aside>
      {selectedTemplate && <form className="email-template-editor" onSubmit={saveTemplate}>
        <div className="email-template-editor-heading"><div><h2>{selectedTemplate.name}</h2><p>Template key: <code>{selectedTemplate.key}</code></p></div><button className="button button-primary" disabled={saving}><Save size={16} /> {saving ? 'Saving...' : 'Save template'}</button></div>
        <label>Subject<input value={draft.subject} onChange={(event) => setDraft((current) => ({ ...current, subject: event.target.value }))} maxLength={250} required /></label>
        <div className="email-template-variables"><strong>Available variables</strong><span>{'{{owner_name}}'}</span><span>{'{{facility_name}}'}</span><span>{'{{pet_name}}'}</span><span>{'{{sender_name}}'}</span><span>{'{{message_preview}}'}</span><span>{'{{conversation_url}}'}</span><span>{'{{app_url}}'}</span><span>{'{{actapet_signature}}'}</span></div>
        <p className="email-template-image-tags">HTML image tags: <code>{'{{actapet_logo}}'}</code> <code>{'{{pet_icon}}'}</code> <code>{'{{pet_image}}'}</code>. The uploaded pet image is included inline when available; otherwise the tag is omitted.</p>
        <label>Plain-text body<textarea value={draft.text_body} onChange={(event) => setDraft((current) => ({ ...current, text_body: event.target.value }))} rows={8} maxLength={20000} required /></label>
        <label>HTML body<textarea className="email-template-html" value={draft.html_body} onChange={(event) => setDraft((current) => ({ ...current, html_body: event.target.value }))} rows={14} maxLength={40000} spellCheck={false} required /></label>
        {notice && <p className="email-template-notice" role="status">{notice}</p>}
      </form>}
    </div>
  </>;
}
